import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Download, Search } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

export default function Products() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const activeCat = params.get("cat") || "all";

  useEffect(() => {
    axios.get(`${API}/products`)
      .then((r) => { setProducts(r.data.products); setCategories(r.data.categories); })
      .catch(() => toast.error("Errore di caricamento", { description: "Impossibile caricare il catalogo. Riprova." }))
      .finally(() => setLoading(false));
  }, []);

  const counts = useMemo(() => {
    const m = {};
    products.forEach((p) => { m[p.category] = (m[p.category] || 0) + 1; });
    return m;
  }, [products]);

  const visible = useMemo(() => {
    const q = search.toLowerCase().trim();
    return products.filter((p) =>
      (activeCat === "all" || p.category === activeCat) &&
      (!q || p.name.toLowerCase().includes(q) || (p.format || "").toLowerCase().includes(q))
    );
  }, [products, activeCat, search]);

  const selectCat = (slug) => {
    if (slug === "all") {
      setParams({}, { replace: true });
    } else {
      navigate(`/prodotti/${slug}`);
      return;
    }
    document.getElementById("products-grid")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const downloadCatalog = async () => {
    const win = window.open("", "_blank");
    try {
      const url = `${API}/catalog.pdf?v=${Date.now()}`;
      const res = await fetch(url, { method: "HEAD" });
      if (res.ok && win) { win.location.href = url; return; }
      if (win) win.close();
      toast.info("Catalogo in preparazione", { description: "Il PDF sarà disponibile a breve. Richiedilo tramite il form contatti." });
    } catch {
      if (win) win.close();
      toast.info("Catalogo in preparazione", { description: "Il PDF sarà disponibile a breve." });
    }
  };

  return (
    <div data-testid="products-page" className="bg-[#faf8f5] text-[#0a192f] min-h-screen">
      <Seo
        title="Catalogo Prodotti Halal all'Ingrosso | Dahmani Food"
        description="Sfoglia il catalogo Dahmani Food: oltre 480 referenze halal in 21 reparti — spezie, datteri, olio, couscous, legumi e specialità etniche all'ingrosso."
        path="/prodotti"
      />
      <Navbar />

      <header className="relative bg-[#0b2545] text-white overflow-hidden">
        <div className="absolute inset-0 df-pattern-gold" />
        <div className="absolute -right-24 -top-24 opacity-[0.12] pointer-events-none">
          <img src="/images/logo.png" alt="" aria-hidden="true" className="w-[420px] h-[420px] rounded-full" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 py-16 md:py-20 flex flex-col md:flex-row md:items-end justify-between gap-8">
          <div className="space-y-4">
            <div className="df-ornament text-xs uppercase font-mono-df tracking-widest">
              <span>Assortimento Halal all'Ingrosso</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-normal tracking-tight">
              Catalogo <span className="italic text-[#d4af37]">Prodotti</span>
            </h1>
            <p className="text-sm text-white/70 max-w-lg">
              {products.length} referenze selezionate in {categories.length} reparti: tocca una categoria o cerca per nome.
            </p>
          </div>
          <button
            data-testid="products-download-catalog"
            onClick={downloadCatalog}
            className="shrink-0 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#d4af37] text-[#06152d] text-xs font-bold uppercase tracking-wider hover:bg-white transition-colors"
          >
            <Download className="w-4 h-4" />
            <span>Scarica PDF</span>
          </button>
        </div>
      </header>

      <div className="sticky top-[68px] z-40 bg-[#faf8f5]/95 backdrop-blur-md border-b border-[#0b2545]/10">
        <div className="max-w-6xl mx-auto px-6 py-4 space-y-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#0b2545]/40" />
            <input
              data-testid="products-search"
              placeholder="Cerca prodotto o formato…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-[#0b2545]/20 bg-white text-sm text-[#0b2545] focus:outline-none focus:border-[#d4af37] transition-colors"
            />
          </div>
          <div data-testid="products-category-bar" className="flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <button
              data-testid="cat-filter-all"
              onClick={() => selectCat("all")}
              className={`shrink-0 px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider border transition-colors ${activeCat === "all" ? "bg-[#0b2545] text-white border-[#0b2545]" : "bg-white text-[#0b2545]/70 border-[#0b2545]/15 hover:border-[#d4af37]"}`}
            >
              Tutti ({products.length})
            </button>
            {categories.filter((c) => counts[c.slug] > 0).map((c) => (
              <button
                key={c.slug}
                data-testid={`cat-filter-${c.slug}`}
                onClick={() => selectCat(c.slug)}
                className={`shrink-0 px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider border transition-colors ${activeCat === c.slug ? "bg-[#0b2545] text-white border-[#0b2545]" : "bg-white text-[#0b2545]/70 border-[#0b2545]/15 hover:border-[#d4af37]"}`}
              >
                {c.label} ({counts[c.slug]})
              </button>
            ))}
          </div>
        </div>
      </div>

      <main id="products-grid" className="max-w-6xl mx-auto px-6 py-12 relative">
        {loading ? (
          <p className="text-center text-sm text-[#0b2545]/60 py-24">Caricamento catalogo…</p>
        ) : visible.length === 0 ? (
          <p data-testid="products-empty" className="text-center text-sm text-[#0b2545]/60 py-24">
            Nessun prodotto trovato. Prova con un altro termine o categoria.
          </p>
        ) : (
          <div data-testid="products-grid-list" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
            {visible.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-20px" }}
                transition={{ duration: 0.4, delay: (i % 5) * 0.05 }}
                data-testid={`product-card-${p.id}`}
                className="group rounded-2xl bg-white border border-[#0b2545]/10 overflow-hidden shadow-sm hover:shadow-lg hover:border-[#d4af37]/50 transition-all duration-300"
              >
                <div className="aspect-square bg-white overflow-hidden">
                  {p.image_url ? (
                    <img
                      src={`${BASE}${p.image_url}`}
                      alt={p.name}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-[#f5efe4]">
                      <img src="/images/logo.png" alt="" className="w-16 h-16 rounded-2xl opacity-60" />
                      <span className="text-[9px] uppercase font-mono-df tracking-widest text-[#0b2545]/40">Foto in arrivo</span>
                    </div>
                  )}
                </div>
                <div className="p-3.5 space-y-1">
                  <p className="text-xs font-bold text-[#0b2545] leading-snug">{p.name}</p>
                  <p className="text-[10px] font-mono-df text-[#c59b27]">
                    {p.category_label}{p.format ? ` · ${p.format}` : ""}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
